/**
 * SHARED with the Cloud Functions (backlog #115).
 *
 * Everything in `shared/` is imported by BOTH halves of the app: the web
 * bundle compiles it with `src/`, and `functions/scripts/build.mjs` inlines it
 * into `functions/lib/index.js` with esbuild, because Firebase uploads only
 * the `functions/` directory. There is no copy and no generated file — one
 * source of truth, edited here.
 *
 * Three rules follow from that:
 *
 * - **Stay pure.** No I/O, no Firebase SDK, no browser or node globals. It has
 *   to run in both places.
 * - **Import nothing but siblings in this directory** — no npm packages (the
 *   functions build leaves bare imports as runtime requires) and never back
 *   into `src/`. Both are lint-enforced and pinned by
 *   `functions/src/__tests__/sharedBoundary.test.ts`.
 * - **Type structurally.** No `src/types` imports; declare the minimum shape
 *   the function needs, so a caller on either side satisfies it.
 *
 * **A change here needs a `functions` deploy**, not just hosting — the CI
 * path filter watches `shared/**` for exactly that reason (Lessons #84, #94).
 */
/**
 * Canonicalize a phone number for comparison and use as a Firestore doc ID.
 *
 * Strips whitespace, dashes, parens, and a leading +65 / 65 country code.
 * Returns the bare digits; empty string for null/empty input.
 *
 * Examples:
 *   "9123 4567"      → "91234567"
 *   "+65 9123-4567"  → "91234567"
 *   "(65) 91234567"  → "91234567"
 *   "91234567"       → "91234567"
 *   ""               → ""
 *
 * The function is intentionally lenient — it does NOT validate that the
 * result is a real Singapore mobile. Callers that need that should add a
 * `/^[89]\d{7}$/` check on top.
 */
export const normalizeMobile = (raw: string | null | undefined): string => {
  if (!raw) return '';
  const digits = raw.replace(/[\s\-()+]/g, '');
  if (digits.length === 10 && digits.startsWith('65')) {
    return digits.slice(2);
  }
  return digits;
};

/** True when the input normalizes to a plausible 8-digit Singapore mobile
 *  (starts with 8 or 9). Narrow check — use only when SG-mobile-specific
 *  validation is required. For general lookup gating use `isLookupReady`. */
export const isPlausibleSgMobile = (raw: string | null | undefined): boolean => {
  return /^[89]\d{7}$/.test(normalizeMobile(raw));
};

/** True when the input has enough digits to be worth a duplicate lookup,
 *  regardless of country. 8 digits matches the form's zod minimum and
 *  covers SG mobiles; longer (e.g. country-code-prefixed foreign numbers)
 *  also passes. Use this in UI to gate debounced lookups so partial entry
 *  doesn't churn the network. */
export const isLookupReady = (raw: string | null | undefined): boolean => {
  return normalizeMobile(raw).length >= 8;
};
